import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Spin, Button, Typography, Layout, Space, Card, Tag, Row, Col, Avatar } from 'antd';
import { 
    DownloadOutlined, 
    GlobalOutlined,
    UserOutlined,
    CalendarOutlined,
    ArrowLeftOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { LARAVEL_BACKEND_API_URL } from '../../Helper';
import fdLogo from '../../assets/images/logo_tm.png';

const { Header, Content } = Layout;
const { Title, Text, Paragraph } = Typography;

const PublicMediaViewer = () => {
    const { type, id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [mediaData, setMediaData] = useState(null);
    const [error, setError] = useState(null);
    const [downloading, setDownloading] = useState(false);

    // Get settings from URL query params
    const queryParams = new URLSearchParams(location.search);
    const showDetails = queryParams.get('show_details') === '1';
    const companyLogo = queryParams.get('logo') || ''; // Company logo from URL
    const orgName = queryParams.get('org') || ''; // Organization name from URL

    useEffect(() => {
        const fetchMedia = async () => {
            try {
                setLoading(true);
                let response;
                if (type === 'video') {
                    response = await axios.get(`${LARAVEL_BACKEND_API_URL}share/media/video/${id}`);
                } else {
                    response = await axios.get(`${LARAVEL_BACKEND_API_URL}share/media/photo/${id}`);
                }

                if (response.data && response.data.success) {
                    setMediaData(response.data.data);
                } else {
                    setError('This media is no longer available or the link is invalid.');
                }
            } catch (err) {
                console.error('Error fetching public media:', err);
                setError('This media is no longer available or the link is invalid.');
            } finally {
                setLoading(false);
            }
        };

        if (id && type) {
            fetchMedia();
        }
    }, [id, type]);

    const handleDownload = () => {
        if (!id || !type) return;
        
        const downloadUrl = `${LARAVEL_BACKEND_API_URL}share/media/download/${type}/${id}`;
        window.open(downloadUrl, '_blank');
    };

    // Determine which logo to show: company logo from URL > API project_info > fallback
    const displayLogo = companyLogo || mediaData?.project_info?.inspector_avatar || fdLogo;
    const displayOrg = orgName || mediaData?.project_info?.organization || 'Floor Detective Professional';

    if (loading) {
        return (
            <div style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#f5f7fa' }}>
                <Space direction="vertical" align="center">
                    <Spin size="large" />
                    <Text type="secondary" style={{ marginTop: 12 }}>Loading shared media...</Text>
                </Space>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#f8f9fc' }}>
                <Card style={{ textAlign: 'center', maxWidth: '400px', borderRadius: '16px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
                    <div style={{ fontSize: '48px', color: '#ff4d4f', marginBottom: '16px' }}>⚠️</div>
                    <Title level={4}>Link Error</Title>
                    <Paragraph type="secondary">{error}</Paragraph>
                    <Button type="primary" shape="round" icon={<ArrowLeftOutlined />} onClick={() => navigate('/')}>Return Home</Button>
                </Card>
            </div>
        );
    }

    if (!mediaData) return null;

    return (
        <Layout style={{ minHeight: '100vh', background: '#f8f9fa' }}>
            <Header style={{ 
                background: '#fff', 
                padding: '0 24px', 
                height: '80px',
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                position: 'sticky',
                top: 0,
                zIndex: 100
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <img 
                        src={displayLogo} 
                        alt="Company Logo"
                        style={{ 
                            height: '70px', 
                            width: 'auto', 
                            objectFit: 'contain',
                            padding: '4px'
                        }} 
                    />
                    <span style={{ fontSize: '15px', color: '#262626' }}>
                        <span style={{ fontWeight: 700 }}>{displayOrg}</span>
                        <span style={{ color: '#8c8c8c', fontWeight: 400 }}> shared a {type} with you</span>
                    </span>
                </div>

                <Button 
                    type="primary" 
                    icon={<DownloadOutlined />} 
                    loading={downloading}
                    onClick={handleDownload}
                    shape="round"
                    style={{ fontWeight: 600, height: '38px', padding: '0 20px', fontSize: '13px' }}
                >
                    Download {type === 'video' ? 'Video' : 'Photo'}
                </Button>
            </Header>

            <Content style={{ padding: '24px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ width: '100%', maxWidth: '1100px', flex: 1 }}>
                    <Card 
                        bodyStyle={{ padding: 0 }}
                        style={{ 
                            borderRadius: '16px', 
                            overflow: 'hidden', 
                            border: 'none', 
                            boxShadow: '0 20px 50px rgba(0,0,0,0.15)',
                            background: '#000',
                            maxWidth: '900px',
                            margin: '0 auto'
                        }}
                    >
                        {/* Media Container */}
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center', 
                            position: 'relative',
                            background: '#000',
                            height: 'calc(100vh - 230px)', 
                            maxHeight: '750px',
                            minHeight: '350px'
                        }}>
                            {type === 'video' ? (
                                <video 
                                    src={mediaData.url} 
                                    controls 
                                    autoPlay
                                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                />
                            ) : (
                                <img 
                                    src={mediaData.url} 
                                    alt="Shared Photo"
                                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                />
                            )}
                        </div>
                        
                        {/* Description Bar */}
                        {mediaData.description && (
                            <div style={{ padding: '20px 24px', background: '#fff' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div>
                                        <Text type="secondary" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                            Description
                                        </Text>
                                        <div style={{ fontSize: '18px', fontWeight: 600, color: '#262626', marginTop: '4px' }}>
                                            {mediaData.description}
                                        </div>
                                    </div>
                                    <Tag color="processing" icon={<CalendarOutlined />} style={{ borderRadius: '6px', padding: '4px 12px' }}>
                                        {mediaData.created_at}
                                    </Tag>
                                </div>
                            </div>
                        )}
                        
                        {/* Minimal date bar for no description */}
                        {!mediaData.description && (
                            <div style={{ padding: '15px 24px', background: '#fff', textAlign: 'right', borderTop: '1px solid #f0f0f0' }}>
                                <Text type="secondary" style={{ fontSize: '13px' }}>
                                    <CalendarOutlined style={{ marginRight: '6px' }} />
                                    Taken on {mediaData.created_at}
                                </Text>
                            </div>
                        )}
                    </Card>

                    {/* Details Section - show_details=1 */}
                    {showDetails && mediaData.project_info && (
                        <div style={{ marginTop: '24px', maxWidth: '900px', margin: '24px auto 0' }}>
                            <Row gutter={[20, 20]}>
                                <Col xs={24} sm={14}>
                                    <div style={{ background: '#fff', padding: '24px', borderRadius: '16px', height: '100%', boxShadow: '0 4px 15px rgba(0,0,0,0.03)' }}>
                                        <div style={{ fontSize: '12px', color: '#bfbfbf', fontWeight: 600, letterSpacing: '0.1em', marginBottom: '8px' }}>PROJECT DETAILS</div>
                                        <Title level={5} style={{ margin: '0 0 16px', fontSize: '18px' }}>{mediaData.project_info.project_name}</Title>
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            <GlobalOutlined style={{ color: '#1890ff', marginTop: '4px', fontSize: '16px' }} />
                                            <Text style={{ color: '#595959', fontSize: '14px', lineHeight: '1.5' }}>{mediaData.project_info.location}</Text>
                                        </div>
                                    </div>
                                </Col>
                                <Col xs={24} sm={10}>
                                    <div style={{ background: '#fff', padding: '24px', borderRadius: '16px', height: '100%', boxShadow: '0 4px 15px rgba(0,0,0,0.03)', display: 'flex', alignItems: 'center', gap: '16px' }}>
                                        <Avatar size={56} src={displayLogo} icon={<UserOutlined />} style={{ background: '#f5f5f5', border: '1px solid #f0f0f0' }} />
                                        <div>
                                            <div style={{ fontSize: '12px', color: '#bfbfbf', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                                                {mediaData.project_info.role_name || 'INSPECTOR'}
                                            </div>
                                            <div style={{ fontSize: '16px', fontWeight: 700, color: '#262626', marginTop: '2px' }}>{mediaData.project_info.inspector_name}</div>
                                            <Text style={{ fontSize: '13px', color: '#8c8c8c' }}>
                                                {mediaData.project_info.role_name ? `Certified ${mediaData.project_info.role_name}` : 'Certified Inspector'}
                                            </Text>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    )}
                </div>

                <div style={{ marginTop: '40px', paddingBottom: '20px', textAlign: 'center' }}>
                    <Text type="secondary" style={{ fontSize: '13px' }}>
                        Powered by <span style={{ fontWeight: 700, color: '#595959' }}>Floor Detective</span>
                    </Text>
                </div>
            </Content>
        </Layout>
    );
};

export default PublicMediaViewer;
